// src/pages/Security/SecurityScoringGuideList.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { FaClipboardList, FaCheckCircle } from 'react-icons/fa';
import TitleWithBack from '../../components/common/TitleWithBack';
import PaginatedTable from '../../components/PaginatedTable';

// Security is the only role that uploads OSM/RLP scoring guides - this list
// exists to get from "which module" to the upload form without needing the
// full Admin module management screens.
function SecurityScoringGuideList() {
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get('/api/modules')
      .then(res => setModules(Array.isArray(res.data) ? res.data : (res.data.content || [])))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const scoringGuideModules = useMemo(
    () => modules.filter((m) => m.examType?.name === 'RLP' || m.examType?.name === 'OSM'),
    [modules]
  );

  const filteredModules = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return scoringGuideModules;
    return scoringGuideModules.filter((m) =>
      (m.moduleName || '').toLowerCase().includes(term) ||
      (m.moduleCode || '').toLowerCase().includes(term)
    );
  }, [scoringGuideModules, search]);

  const columns = [
    { key: 'serial', header: <>#</> },
    { key: 'moduleName', header: 'Module Name' },
    { key: 'moduleCode', header: 'Module Code' },
    { key: 'examType', header: 'Exam Type' },
    { key: 'program', header: 'Program' },
    { key: 'actions', header: 'Actions' }
  ];

  const renderRow = (mod, idx, pageIndex, pageSize) => (
    <>
      <td>{(pageIndex * pageSize) + idx + 1}</td>
      <td>
        {mod.moduleName || 'N/A'}
        {mod.hasScoringGuide && (
          <FaCheckCircle className="ms-1 text-success" title="Scoring guide uploaded" />
        )}
      </td>
      <td>{mod.moduleCode || 'N/A'}</td>
      <td>{mod.examType?.name || 'N/A'}</td>
      <td>{mod.program?.name || 'N/A'}</td>
      <td>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => navigate(`/security/scoring-guides/${mod.id}`)}
        >
          <FaClipboardList className="me-1" />
          Scoring Guide
        </button>
      </td>
    </>
  );

  return (
    <Container className="mt-2" fluid>
      <TitleWithBack title="Upload Scoring Guides" />
      <Row className="mb-3">
        <Col md={4}>
          <Form.Control
            type="text"
            placeholder="Search by module name or code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
      </Row>
      {loading ? (
        <p>Loading...</p>
      ) : filteredModules.length === 0 ? (
        <p>Nothing to show here</p>
      ) : (
        <PaginatedTable data={filteredModules} columns={columns} renderRow={renderRow} rowsPerPage={20} />
      )}
    </Container>
  );
}

export default SecurityScoringGuideList;
